<script setup lang="ts">
/** One Social wall post card (SocialWallPreview.vue), as the players draw it: header, text, picture, time ago. */
import { computed, ref } from 'vue';
import { isRtlText, postInitials, SOCIAL_PLATFORMS, timeAgo, type SocialPost, type SocialWallWidget } from '@codot/contracts';

const props = withDefaults(defineProps<{ post: SocialPost; config: SocialWallWidget; font: string; now: number; big?: boolean }>(), { big: false });
/** Pictures and avatars whose link failed: the card goes on without them. */
const broken = ref(new Set<string>());
const fail = (url: string) => (broken.value = new Set(broken.value).add(url));
const ok = (url: string | null): url is string => !!url && !broken.value.has(url);
const p = computed(() => props.post);
const dir = computed(() => (isRtlText(p.value.text) || (!p.value.text && props.config.language === 'ar') ? 'rtl' : 'ltr'));
const badge = computed(() => (props.config.showPlatform ? SOCIAL_PLATFORMS[p.value.platform] : undefined));
const ago = computed(() => timeAgo(p.value.at, props.now, props.config.language));
const textStyle = computed(() => ({
  lineHeight: 1.3,
  fontSize: props.big ? '1.35em' : undefined,
  display: '-webkit-box',
  WebkitLineClamp: props.big ? 8 : p.value.image ? 3 : 7,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
}));
</script>

<template>
  <div class="sw-card" :dir="dir" :style="{ fontSize: font, borderTopColor: config.accent }">
    <div class="flex min-w-0 shrink-0 items-center" style="gap: 0.55em">
      <div class="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full" :style="{ width: '2.4em', height: '2.4em', background: config.accent }">
        <span class="font-bold text-white" style="font-size: 0.95em">{{ postInitials(p.author || p.handle) || '•' }}</span>
        <img v-if="ok(p.avatar)" :src="p.avatar" alt="" class="absolute inset-0 h-full w-full object-cover" @error="fail(p.avatar)" />
      </div>
      <div class="min-w-0 flex-1">
        <p v-if="p.author" class="truncate font-bold">{{ p.author }}</p>
        <p v-if="p.handle" class="truncate opacity-70" style="font-size: 0.78em" dir="ltr">{{ p.handle }}</p>
      </div>
      <span v-if="badge" class="sw-badge" :style="{ background: badge.color }">{{ badge.label }}</span>
    </div>
    <p v-if="p.text" class="shrink-0" :style="textStyle">{{ p.text }}</p>
    <div v-if="ok(p.image)" class="min-h-0 flex-1 overflow-hidden" style="border-radius: 0.4em"><img :src="p.image" alt="" class="h-full w-full object-cover" @error="fail(p.image)" /></div>
    <div v-else class="flex-1" />
    <p v-if="ago" class="shrink-0 opacity-60" style="font-size: 0.72em">{{ ago }}</p>
  </div>
</template>

<style scoped>
.sw-card {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 0.5em;
  overflow: hidden;
  padding: 0.8em;
  border-radius: 0.6em;
  background: rgba(127, 127, 127, 0.16);
  border-top: 0.18em solid;
}
.sw-badge {
  flex-shrink: 0;
  white-space: nowrap;
  border-radius: 999px;
  padding: 0.25em 0.7em;
  font-size: 0.62em;
  font-weight: 700;
  color: #fff;
}
</style>
